import { useEffect, useRef } from 'react';

// Company members tables are wider than the page, so their body scrolls sideways in its own
// overflow box, and that box stops a sticky <thead> from pinning to <main>. The page instead pins
// a separate strip of column headings above the body. This keeps the strip lined up with the
// body: it follows the body's sideways scroll, and copies the widths of the body table's own
// (visually collapsed) header cells onto the strip's <col>s.
export const useStickyColumnHeader = () => {
  const stripRef = useRef<HTMLDivElement>(null);
  const bodyScrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const strip = stripRef.current;
    const bodyScroller = bodyScrollerRef.current;
    const bodyHeaderRow =
      bodyScroller?.querySelector<HTMLTableRowElement>('thead tr');
    const bodyTable = bodyHeaderRow?.closest('table');
    if (!strip || !bodyScroller || !bodyHeaderRow || !bodyTable) {
      return undefined;
    }

    const syncScroll = () => {
      strip.scrollLeft = bodyScroller.scrollLeft;
    };

    const syncWidths = () => {
      const stripTable = strip.querySelector('table');
      const stripCols = strip.querySelectorAll('col');
      Array.from(bodyHeaderRow.cells).forEach((cell, index) => {
        const col = stripCols[index];
        if (col) col.style.width = `${cell.getBoundingClientRect().width}px`;
      });
      if (stripTable) {
        stripTable.style.width = `${bodyTable.getBoundingClientRect().width}px`;
      }
      syncScroll();
    };

    syncWidths();
    bodyScroller.addEventListener('scroll', syncScroll);
    const observer = new ResizeObserver(syncWidths);
    observer.observe(bodyTable);
    Array.from(bodyHeaderRow.cells).forEach((cell) => observer.observe(cell));

    return () => {
      bodyScroller.removeEventListener('scroll', syncScroll);
      observer.disconnect();
    };
  }, []);

  return { bodyScrollerRef, stripRef };
};
